// Node access system — docs/ARCHITECTURE_M0.md §5.4.
// Qualification consumes implementation EM ahead of projects; yield research next.
// Iteration follows content.nodes order (never Object.keys) for determinism.

import type { TickContext } from "../ctx.js";
import { emit } from "../ctx.js";
import { foundryDelta } from "./stakeholders.js";

export function processNodes(ctx: TickContext): void {
  const { s, content } = ctx;
  const models = content.economy.models;

  // 1) qualification projects (reserved ahead of everything else)
  for (const node of content.nodes) {
    const access = s.nodes[node.id]!;
    if (access.status !== "qualifying") continue;
    const perMonthNeed = access.qualEmRemaining / Math.max(1, access.qualMonthsRemaining);
    const want = perMonthNeed * ctx.monthsPerTick;
    const consumed = Math.min(want, ctx.emBudget.implementation);
    ctx.emBudget.implementation -= consumed;
    ctx.allocation.implementation.qual += consumed;
    access.qualEmRemaining = Math.max(0, access.qualEmRemaining - consumed);
    access.qualMonthsRemaining = Math.max(0, access.qualMonthsRemaining - ctx.monthsPerTick);
    if (access.qualEmRemaining <= 1e-9 && access.qualMonthsRemaining <= 0) {
      access.status = "qualified";
      access.qualEmRemaining = 0;
      // qualifying their next node is the strongest signal an account can send (M3 §11)
      foundryDelta(ctx, "nodeQualified");
      emit(ctx, "qualComplete", `${node.displayName} qualified`, { nodeId: node.id });
    }
  }

  // 2) yield research — no cap: the gap model asymptotes on its own (tau = yieldGapTauEm)
  for (const node of content.nodes) {
    const access = s.nodes[node.id]!;
    if (access.status !== "qualified" || access.researchStaff <= 0) continue;
    // past ~5 tau the gap is closed; stop drawing EM so staff isn't silently wasted
    if (access.researchEm >= 5 * models.yieldGapTauEm) continue;
    const want = access.researchStaff * ctx.monthsPerTick;
    const consumed = Math.min(want, ctx.emBudget.implementation);
    ctx.emBudget.implementation -= consumed;
    ctx.allocation.implementation.research += consumed;
    access.researchEm += consumed;
  }

  // 3) reliability engineering (design §6.5, split from yield research in M1):
  // qual rigor/burn-in per node — buys down the field-failure hazard, not D0
  for (const node of content.nodes) {
    const access = s.nodes[node.id]!;
    if (access.status !== "qualified" || access.reliabilityStaff <= 0) continue;
    if (access.reliabilityEm >= 5 * models.reliabilityTauEm) continue;
    const want = access.reliabilityStaff * ctx.monthsPerTick;
    const consumed = Math.min(want, ctx.emBudget.implementation);
    ctx.emBudget.implementation -= consumed;
    ctx.allocation.implementation.reliability += consumed;
    access.reliabilityEm += consumed;
  }
}
